import { prefersReducedMotion } from '@theme/utilities';

const DURATION_MS = 1400;
const NUMBER = /-?\d[\d,]*(?:\.\d+)?/;

/**
 * Counts a stat value up from 0 when it scrolls into view. The full value is
 * server-rendered ("+289%"); this parses out the number and animates only the
 * numeric run, keeping prefix/suffix ("+", "%") intact. Runs once. Without JS
 * or under reduced motion the rendered value simply stays.
 */
class StatCount extends HTMLElement {
  #observer = null;
  #raf = null;
  /** Pristine server-rendered text — never re-parse our own written output
      (an editor DOM move reconnects the element mid-count). */
  #source = null;
  #done = false;
  #prefix = '';
  #suffix = '';
  #target = null;
  #decimals = 0;
  #grouped = false;

  connectedCallback() {
    if (prefersReducedMotion()) return;

    if (this.#source === null) {
      this.#source = this.textContent;
      const match = this.#source.match(NUMBER);
      if (!match) return;
      this.#prefix = this.#source.slice(0, match.index);
      this.#suffix = this.#source.slice(match.index + match[0].length);
      this.#grouped = match[0].includes(',');
      this.#decimals = (match[0].split('.')[1] || '').length;
      const target = parseFloat(match[0].replace(/,/g, ''));
      if (Number.isFinite(target)) this.#target = target;
    }
    if (this.#target === null) return;

    if (this.#done) {
      this.textContent = this.#source;
      return;
    }

    this.#write(0);
    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.3 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#raf = null;
    // Leave the pristine value in the DOM so a moved/cloned node reads clean.
    if (this.#source !== null && this.#target !== null) {
      this.textContent = this.#source;
    }
  }

  #onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#observer?.disconnect();
    this.#observer = null;
    if (!this.isConnected) return;

    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / DURATION_MS, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      if (progress < 1) {
        this.#write(this.#target * eased);
        this.#raf = requestAnimationFrame(tick);
      } else {
        this.textContent = this.#source;
        this.#done = true;
        this.#raf = null;
      }
    };
    this.#raf = requestAnimationFrame(tick);
  };

  #write(value) {
    let formatted = value.toFixed(this.#decimals);
    if (this.#grouped) {
      formatted = Number(formatted).toLocaleString('en-US', {
        minimumFractionDigits: this.#decimals,
        maximumFractionDigits: this.#decimals,
      });
    }
    this.textContent = this.#prefix + formatted + this.#suffix;
  }
}

if (!customElements.get('stat-count')) {
  customElements.define('stat-count', StatCount);
}
